// =====================================================================
// InvitationsTab.jsx — M-011 (26-08-2026) — Onboarding contrats par QR
// =====================================================================
// Onglet « Invitations » de /admin/users :
//  - Inviter (CDD ou CDI) : nouveau OU collaborateur connu (typeahead sur
//    TOUS les users, y compris masqués) → QR + lien à usage unique 48h,
//    envoi auto par mail si adresse connue.
//  - File de validation : dossier soumis → « Valider (créer le compte) »
//    ou « Refuser ». La création de compte n'est JAMAIS automatique.
// =====================================================================
import { useEffect, useMemo, useRef, useState } from 'react';
import { onboarding, users as usersApi, errMsg } from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { DROITS_LIRE_DOSSIER } from '../../lib/droits-dossier.js';
import { ficheReservee } from '../../lib/roles.js';
import { motsDuNom, formesDuNom, nettoyerNom, estUnNom, pairesEnDouble, clePaire, messageNomsEnDouble } from '../../lib/noms.js';

const STATUS_FR = {
  PENDING: { label: 'En attente du candidat', color: '#b45309', bg: '#fffbeb' },
  SUBMITTED: { label: 'Dossier soumis — À VALIDER', color: '#1d4ed8', bg: '#eff6ff' },
  VALIDATED: { label: 'Validée — compte créé', color: '#15803d', bg: '#f0fdf4' },
  REFUSED: { label: 'Refusée', color: '#b91c1c', bg: '#fef2f2' },
  EXPIRED: { label: 'Expirée (48h)', color: '#64748b', bg: '#f8fafc' },
  CANCELLED: { label: 'Annulée', color: '#64748b', bg: '#f8fafc' },
};

export default function InvitationsTab({ groups }) {
  const { can } = useAuth();
  // La liste des comptes ne sert qu'à « Collaborateur connu » : chargée seulement
  // pour qui peut l'utiliser.
  const peutConnu = can(DROITS_LIRE_DOSSIER);
  // La liste vient d'ICI, pas de l'onglet Utilisateurs. Elle y était reprise
  // telle quelle — donc déjà réduite par le rôle, l'équipe, la recherche ou le
  // filtre actif/inactif qu'un admin avait laissés. Un collaborateur hors de ce
  // sous-ensemble restait introuvable, et la seule sortie était « Nouveau
  // collaborateur » : un SECOND compte pour la même personne.
  const [users, setUsers] = useState(null);           // null = pas encore chargée
  const [usersErr, setUsersErr] = useState('');
  // Chargée à l'ouverture du formulaire (voir plus bas) : un compte créé par une
  // validation doit être trouvable dans « Collaborateur connu », sinon on recrée un
  // doublon. Seule la réponse la plus récente compte.
  const derniereRequeteComptes = useRef(0);
  const chargerComptes = () => {
    if (!peutConnu) { setUsers([]); return; }
    // Pendant le rechargement : « Chargement… », pas la liste précédente (elle n'a pas
    // encore un compte créé entre-temps → doublon).
    setUsers(null); setUsersErr('');
    const n = ++derniereRequeteComptes.current;
    usersApi.list({ champs: 'minimal' })   // nom, identifiant, email, statut : pas de dossier
      .then((d) => { if (n === derniereRequeteComptes.current) { setUsers(Array.isArray(d) ? d : (d.items || d.users || [])); setUsersErr(''); } })
      // Une liste vide SILENCIEUSE referait exactement le doublon : l'admin ne
      // trouve personne et crée un second compte. Si ça échoue, on le dit.
      .catch((e) => { if (n === derniereRequeteComptes.current) { setUsers([]); setUsersErr(errMsg(e)); } });
  };
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);      // création → QR à afficher UNE fois
  const [validated, setValidated] = useState(null); // validation → identifiants éventuels
  // Demande d'intérimaires (26-08-2026, variante B — groupe choisi dans la demande)
  const [interimCreating, setInterimCreating] = useState(false);
  const [interimConfirming, setInterimConfirming] = useState(null); // invite à confirmer (saisie des noms)
  const [interimCreds, setInterimCreds] = useState(null);           // comptes créés → identifiants affichés UNE fois
  // Liste chargée (fraîche) à CHAQUE ouverture du formulaire « Inviter » : seul
  // « Collaborateur connu » s'en sert, et un compte créé entre-temps y figure.
  useEffect(() => { if (creating) chargerComptes(); }, [creating]);

  // `silencieux` : la liste reste affichée pendant le rechargement (fermeture d'une
  // fenêtre : pas de clignotement, la position dans la page reste).
  // Seule la réponse de la DERNIÈRE demande est gardée (une ancienne arrivée en retard
  // ne réaffiche pas une ligne annulée entre-temps).
  const derniereListe = useRef(0);
  const load = ({ silencieux = false } = {}) => {
    const n = ++derniereListe.current;
    if (!silencieux) setLoading(true);
    onboarding.list()
      .then((liste) => { if (n === derniereListe.current) setInvites(liste); })
      .catch(() => { if (n === derniereListe.current && !silencieux) setInvites([]); })
      .finally(() => { if (n === derniereListe.current) setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  // Ce que CE compte peut valider (pas les dossiers réservés).
  const toValidate = invites.filter((i) => i.status === 'SUBMITTED' && !i.reserve);

  const handleValidate = async (inv) => {
    if (!window.confirm(`Valider le dossier de ${inv.candidateName} et créer/mettre à jour son compte ?\n\nLes identifiants seront envoyés par email au candidat.`)) return;
    try {
      const r = await onboarding.validate(inv.id);
      setValidated(r);
      load();
    } catch (e) { alert(errMsg(e)); }
  };
  const handleRefuse = async (inv) => {
    const reason = window.prompt(`Refuser le dossier de ${inv.candidateName} ?\nMotif (optionnel) :`);
    if (reason === null) return;
    try { await onboarding.refuse(inv.id, reason); load(); } catch (e) { alert(errMsg(e)); }
  };
  const handleCancel = async (inv) => {
    if (!window.confirm(`Annuler l'invitation de ${inv.candidateName || '(sans nom)'} ? Le lien ne fonctionnera plus.`)) return;
    try { await onboarding.cancel(inv.id); load(); } catch (e) { alert(errMsg(e)); }
  };

  return (
    <div>
      <div className="page-header">
        <h1>Invitations d'embauche <span className="count-badge">{invites.length}</span></h1>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="btn" onClick={() => setInterimCreating(true)}>👷 Demander des intérimaires</button>
          <button className="btn btn-primary" onClick={() => setCreating(true)}>📨 Inviter (CDD ou CDI)</button>
        </div>
      </div>
      <p style={{ color: '#64748b', fontSize: 13, marginTop: -6 }}>
        Le candidat complète lui-même son dossier (Carte ID incluse) via un lien/QR à usage unique valable 48h.
        Le compte n'est créé <strong>qu'après votre validation</strong> ci-dessous.
      </p>

      {toValidate.length > 0 && (
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
          <strong style={{ color: '#1d4ed8' }}>🔔 {toValidate.length} dossier(s) en attente de validation</strong>
        </div>
      )}

      {loading ? <div className="loading">Chargement…</div> : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Candidat</th><th>Contrat</th><th>Statut</th><th>Créée le</th><th></th>
              </tr>
            </thead>
            <tbody>
              {invites.map((inv) => {
                const d = inv.submittedData ? JSON.parse(inv.submittedData) : null;
                const isInterim = inv.contractType === 'INTERIM';
                let st = STATUS_FR[inv.status] || { label: inv.status, color: '#334155', bg: '#f8fafc' };
                if (isInterim && inv.status === 'PENDING') st = { label: 'Demande envoyée — en attente de confirmation', color: '#b45309', bg: '#fffbeb' };
                if (isInterim && inv.status === 'VALIDATED') st = { label: 'Confirmée — comptes prêts', color: '#15803d', bg: '#f0fdf4' };
                return (
                  <tr key={inv.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{inv.candidateName || <span style={{ color: '#94a3b8' }}>—</span>}</div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>
                        {isInterim
                          ? (d?.groupName ? `Équipe : ${d.groupName}` : 'demande vers l\'agence')
                          : (inv.candidateEmail || 'pas d\'email — lien à transmettre (WhatsApp/QR)')}
                      </div>
                    </td>
                    <td>
                      {isInterim ? 'Intérim' : inv.contractType}
                      {inv.contractStart && <div style={{ fontSize: 12, color: '#64748b' }}>{inv.contractStart}{inv.contractEnd ? ` → ${inv.contractEnd}` : ''}</div>}
                      {inv.knownUserId && <span style={{ fontSize: 11, background: '#f1f5f9', borderRadius: 4, padding: '1px 6px' }}>connu</span>}
                    </td>
                    <td><span style={{ fontSize: 12, fontWeight: 600, color: st.color, background: st.bg, borderRadius: 6, padding: '3px 8px' }}>{st.label}</span></td>
                    <td style={{ fontSize: 12, color: '#64748b' }}>{(inv.createdAt || '').slice(0, 16).replace('T', ' ')}</td>
                    <td className="actions" style={{ whiteSpace: 'nowrap' }}>
                      {isInterim && inv.status === 'PENDING' && (
                        <>
                          <button className="btn btn-primary" style={{ fontSize: 12, marginRight: 6 }} onClick={() => setInterimConfirming(inv)}>
                            ✅ Confirmé → valider les comptes
                          </button>
                          <button className="btn" style={{ fontSize: 12, color: '#b91c1c' }} onClick={() => handleCancel(inv)}>Annuler</button>
                        </>
                      )}
                      {!isInterim && (inv.status === 'SUBMITTED' || inv.status === 'PENDING') && inv.reserve && (
                        <span style={{ fontSize: 12, color: '#b45309' }}
                          title="Collaborateur déjà connu : son dossier, la validation, le refus et l'annulation sont réservés à qui peut lire tous les comptes (et, pour un accès total, à un accès total).">
                          Collaborateur connu — réservé à un administrateur
                        </span>
                      )}
                      {!isInterim && inv.status === 'SUBMITTED' && !inv.reserve && (
                        <>
                          {d && (
                            <button className="btn" style={{ fontSize: 12, marginRight: 6 }} onClick={() => alert(
                              `Dossier de ${d.name}\n\nEmail : ${d.email}\nTéléphone : ${d.phone || '—'}\nLieu de naissance : ${d.birthPlace}\nAdresse : ${d.address}\nIBAN : ${d.iban || '(inchangé — déjà au dossier)'}\nPers. à charge : ${d.dependentsCount}\nÉtat civil : ${d.maritalStatus}${d.spouseName ? `\nConjoint : ${d.spouseName} (travaille : ${d.spouseWorks === 'true' ? 'oui' : 'non'})` : ''}`
                            )}>👁 Dossier</button>
                          )}
                          {inv.ciRectoPath && <a href={onboarding.ciUrl(inv.id, 'recto')} target="_blank" rel="noreferrer" style={{ fontSize: 12, marginRight: 6 }}>Carte ID recto</a>}
                          {inv.ciVersoPath && <a href={onboarding.ciUrl(inv.id, 'verso')} target="_blank" rel="noreferrer" style={{ fontSize: 12, marginRight: 6 }}>verso</a>}
                          <button className="btn btn-primary" style={{ fontSize: 12, marginRight: 6 }} onClick={() => handleValidate(inv)}>✅ Valider → créer le compte</button>
                          <button className="btn" style={{ fontSize: 12, color: '#b91c1c' }} onClick={() => handleRefuse(inv)}>Refuser</button>
                        </>
                      )}
                      {!isInterim && inv.status === 'PENDING' && !inv.reserve && (
                        <button className="btn" style={{ fontSize: 12, color: '#b91c1c' }} onClick={() => handleCancel(inv)}>Annuler</button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {invites.length === 0 && <tr><td colSpan={5} style={{ color: '#94a3b8', textAlign: 'center', padding: 24 }}>Aucune invitation.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {creating && <InviteModal users={users} usersErr={usersErr} onClose={() => setCreating(false)} onCreated={(r) => { setCreating(false); setResult(r); load(); }} />}
      {result && <QrResultModal result={result} onClose={() => setResult(null)} />}
      {validated && <ValidatedModal data={validated} onClose={() => setValidated(null)} />}
      {interimCreating && <InterimRequestModal groups={groups} onClose={() => setInterimCreating(false)} onCreated={() => { setInterimCreating(false); load(); }} />}
      {interimConfirming && (
        <InterimConfirmModal invite={interimConfirming} onClose={() => { setInterimConfirming(null); load({ silencieux: true }); }}
          onDone={(r) => { setInterimConfirming(null); setInterimCreds(r); load(); }} onRefresh={() => load({ silencieux: true })} />
      )}
      {interimCreds && <InterimCredsModal data={interimCreds} onClose={() => setInterimCreds(null)} />}
    </div>
  );
}

// --------------------------- Création ---------------------------------
function InviteModal({ users, usersErr, onClose, onCreated }) {
  // « Collaborateur connu » montre son dossier (pré-rempli) : réservé, comme côté
  // serveur, à qui peut lire tous les comptes ou les modifier.
  const { can, user: moi } = useAuth();
  const peutConnu = can(DROITS_LIRE_DOSSIER);
  const [mode, setMode] = useState('new');      // 'new' | 'known'
  const [query, setQuery] = useState('');
  const [known, setKnown] = useState(null);     // user sélectionné
  const [f, setF] = useState({ contractType: 'CDD', contractStart: '', contractEnd: '', candidateName: '', candidateEmail: '', motif: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  // 30-09-2026 — dossier incomplet : la prolongation est refusée (la chaîne contrats
  // ne pourrait rien en faire). Il FAUT alors une sortie : le formulaire complet
  // lien/QR pour CE collaborateur. Sans elle, l'admin n'avait plus que « Nouveau »,
  // qui crée un DOUBLON de compte. Mesuré le 30-09 : 21 des 28 CDD actifs ont un
  // dossier incomplet — ce cas est la règle, pas l'exception.
  const [manque, setManque] = useState(null);   // champs manquants au dossier

  // Typeahead sur TOUS les users (y compris masqués/inactifs) — demande Silviu 26/08
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    // Les 8 places vont d'abord aux comptes ACTIFS : la liste etant desormais
    // purement alphabetique, un homonyme eteint pouvait evincer le bon compte
    // et pousser vers « Nouveau collaborateur » — c'est-a-dire vers un doublon.
    // Un accès total reste AFFICHÉ (sinon on croit qu'il n'existe pas et on crée un
    // doublon), mais réservé : non sélectionnable, avec la raison.
    const trouves = (users || []).filter((u) => u.name?.toLowerCase().includes(q) || u.username?.toLowerCase().includes(q));
    return [...trouves.filter((u) => u.isActive !== false), ...trouves.filter((u) => u.isActive === false)].slice(0, 8);
  }, [query, users, moi]);

  // Repli quand le dossier est incomplet : invitation AVEC formulaire (lien/QR) pour
  // le collaborateur connu. knownUserId est conservé → formulaire prérempli et la
  // validation met à jour SA fiche, sans créer de second compte.
  const envoyerFormulaireComplet = async () => {
    setErr(''); setSaving(true);
    try {
      const r = await onboarding.create({
        contractType: f.contractType,
        contractStart: f.contractStart,
        contractEnd: f.contractType === 'CDD' ? f.contractEnd : null,
        knownUserId: known.id,
        candidateName: known.name,
        candidateEmail: known.email || f.candidateEmail.trim() || null,
      });
      onCreated(r);
    } catch (e2) { setErr(errMsg(e2)); } finally { setSaving(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setManque(null);
    if (!f.contractStart) { setErr('Date de début obligatoire.'); return; }
    if (f.contractType === 'CDD' && (!f.contractEnd || f.contractStart > f.contractEnd)) { setErr('CDD : période obligatoire (début ≤ fin).'); return; }
    if (mode === 'known' && !known) { setErr('Sélectionnez le collaborateur connu.'); return; }
    setSaving(true);
    try {
      // 30-09-2026 — « Collaborateur connu » = PROLONGATION : son dossier est déjà
      // complet, donc pas de QR et pas de formulaire à remplir. L'info part directement
      // par mail : au collaborateur (confirmation 1 clic + guide d'encodage) et à la
      // chaîne contrats. C'est l'absence de ce chemin qui faisait contourner Zeus.
      if (mode === 'known') {
        const r = await onboarding.prolongation({
          knownUserId: known.id,
          contractType: f.contractType,
          contractStart: f.contractStart,
          contractEnd: f.contractType === 'CDD' ? f.contractEnd : null,
          motif: f.motif?.trim() || null,
          // Dossier sans email : l'adresse saisie ici sert à CET envoi (le backend
          // ne l'écrit pas dans la fiche). Sans ça, le champ resterait décoratif.
          candidateEmail: known.email ? null : (f.candidateEmail.trim() || null),
        });
        onCreated({ ...r, prolongation: true });
        return;
      }
      const r = await onboarding.create({
        contractType: f.contractType,
        contractStart: f.contractStart,
        contractEnd: f.contractType === 'CDD' ? f.contractEnd : null,
        knownUserId: null,
        candidateName: f.candidateName.trim() || null,
        candidateEmail: f.candidateEmail.trim() || null,
      });
      onCreated(r);
    } catch (e2) {
      // ⚠️ error-handler.js renvoie le code dans le champ « error », pas « code ».
      const d = e2?.response?.data || {};
      if ((d.error || d.code) === 'DOSSIER_INCOMPLETE') setManque(d.details?.missing || []);
      setErr(errMsg(e2));
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Inviter — dossier d'embauche</h2>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body">
            {err && <div className="alert alert-error">{err}</div>}

            {/* Dossier incomplet : on NE laisse PAS l'admin dans une impasse. La seule
                autre option visible serait « Nouveau », qui créerait un compte en double. */}
            {manque && known && (
              <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 13 }}>
                <div style={{ marginBottom: 6 }}>
                  Le dossier de <strong>{known.name}</strong> n'est pas complet
                  {manque.length ? <> — il manque : <strong>{manque.join(', ')}</strong></> : null}.
                  Une prolongation ne peut pas partir sans ça.
                </div>
                <div style={{ marginBottom: 8 }}>
                  Envoyez-lui le <strong>formulaire complet</strong> : il complète lui-même ce qui manque,
                  puis vous validez. C'est bien <strong>son</strong> compte qui sera mis à jour — aucun doublon.
                </div>
                <button type="button" className="btn btn-primary" disabled={saving}
                  onClick={envoyerFormulaireComplet}>
                  {saving ? 'Envoi…' : 'Envoyer le formulaire complet (lien + QR)'}
                </button>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              {[['new', 'Nouveau collaborateur'], ['known', 'Collaborateur connu (a déjà travaillé ici)']].map(([m, label]) => (
                <button key={m} type="button" onClick={() => { setMode(m); setKnown(null); setQuery(''); setManque(null); }}
                  disabled={m === 'known' && !peutConnu}
                  title={m === 'known' && !peutConnu ? "Réservé à qui peut lire tous les comptes (le dossier est pré-rempli). Demandez à un administrateur — ne créez pas un second compte." : undefined}
                  style={{
                    flex: 1, padding: '8px 6px', borderRadius: 8, fontSize: 13, cursor: (m === 'known' && !peutConnu) ? 'not-allowed' : 'pointer',
                    opacity: (m === 'known' && !peutConnu) ? 0.55 : 1,
                    border: mode === m ? '2px solid #2563eb' : '1px solid #cbd5e1',
                    background: mode === m ? '#eff6ff' : '#fff', fontWeight: mode === m ? 700 : 400,
                  }}>{label}</button>
              ))}
            </div>
            {!peutConnu && (
              <p style={{ fontSize: 12, color: '#b45309', margin: '-4px 0 12px' }}>
                « Collaborateur connu » est réservé à qui peut lire tous les comptes (son dossier est pré-rempli).
                Pour quelqu'un qui a déjà travaillé ici, demandez à un administrateur : ne créez pas un second compte.
              </p>
            )}

            {mode === 'known' && !known && (
              <div className="form-row" style={{ position: 'relative' }}>
                <label>Rechercher le collaborateur <small style={{ color: '#94a3b8' }}>(y compris masqués)</small></label>
                {usersErr
                  ? <div style={{ color: '#b91c1c', fontSize: 13, padding: '6px 0' }}>La liste des collaborateurs n'a pas pu être chargée ({usersErr}). Ne créez pas de nouveau compte avant d'avoir vérifié : la personne existe peut-être déjà.</div>
                  : users === null
                    ? <div style={{ color: '#64748b', fontSize: 13, padding: '6px 0' }}>Chargement de la liste…</div>
                    : <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Tapez le nom…" autoFocus />}
                {matches.length > 0 && (
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, marginTop: 4, background: '#fff', maxHeight: 220, overflowY: 'auto' }}>
                    {matches.map((u) => (
                      <div key={u.id} onClick={ficheReservee(u, moi) ? undefined : () => { setKnown(u); setQuery(''); setManque(null); }}
                        style={{ padding: '8px 12px', cursor: ficheReservee(u, moi) ? 'not-allowed' : 'pointer', borderBottom: '1px solid #f1f5f9', opacity: ficheReservee(u, moi) ? 0.6 : 1 }}>
                        <strong>{u.name}</strong> <span style={{ color: '#64748b', fontSize: 12 }}>({u.username})</span>
                        {ficheReservee(u, moi) && <span style={{ fontSize: 11, color: '#b45309', marginLeft: 6 }}>accès total — à faire par un accès total (ne créez pas un second compte)</span>}
                        {u.excludeFromPresence && <span style={{ fontSize: 11, color: '#b45309', marginLeft: 6 }}>masqué</span>}
                        {!u.isActive && <span style={{ fontSize: 11, color: '#b91c1c', marginLeft: 6 }}>inactif</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {mode === 'known' && known && (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 12px', marginBottom: 10, fontSize: 14 }}>
                ✅ <strong>{known.name}</strong> — <strong>prolongation</strong> : son dossier est déjà complet,
                donc <strong>pas de QR ni de formulaire</strong>. À l'envoi, deux mails partent tout de suite :
                à lui (période + confirmation en 1 clic + guide d'encodage des timesheets) et à l'équipe contrats.
                {!known.email && <div style={{ color: '#b45309', fontSize: 12, marginTop: 4 }}>⚠️ Pas d'email au dossier — il ne recevra pas la confirmation. La demande partira quand même à l'équipe contrats.</div>}
                <button type="button" className="btn" style={{ fontSize: 12, marginLeft: 8 }} onClick={() => { setKnown(null); setManque(null); }}>changer</button>
              </div>
            )}

            {mode === 'new' && (
              <div className="form-row">
                <label>Nom du candidat <small style={{ color: '#94a3b8' }}>(optionnel — étiquette)</small></label>
                <input type="text" value={f.candidateName} onChange={(e) => setF({ ...f, candidateName: e.target.value })} />
              </div>
            )}

            <div className="form-row">
              <label>
                Email du {mode === 'known' ? 'collaborateur' : 'candidat'}{' '}
                <small style={{ color: '#94a3b8' }}>
                  {mode === 'known' ? '(reprend celui du dossier — sert à la confirmation)' : '(si présent : le lien part automatiquement par mail)'}
                </small>
              </label>
              <input type="email" value={mode === 'known' && known?.email ? known.email : f.candidateEmail}
                disabled={mode === 'known' && !!known?.email}
                onChange={(e) => setF({ ...f, candidateEmail: e.target.value })} />
            </div>

            <div className="form-row">
              <label>Type de contrat *</label>
              <select value={f.contractType} onChange={(e) => setF({ ...f, contractType: e.target.value })}>
                <option value="CDD">CDD</option>
                <option value="CDI">CDI</option>
              </select>
            </div>
            <div className="form-row" style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label>Début contrat *</label>
                <input type="date" value={f.contractStart} onChange={(e) => setF({ ...f, contractStart: e.target.value })} required />
              </div>
              {f.contractType === 'CDD' && (
                <div style={{ flex: 1 }}>
                  <label>Fin contrat *</label>
                  <input type="date" value={f.contractEnd} onChange={(e) => setF({ ...f, contractEnd: e.target.value })} required />
                </div>
              )}
            </div>
            {mode === 'known' && (
              <div className="form-row">
                <label>Motif de la prolongation <small style={{ color: '#94a3b8' }}>(optionnel — apparaît dans le mail à l'équipe contrats)</small></label>
                <input type="text" value={f.motif} maxLength={300}
                  onChange={(e) => setF({ ...f, motif: e.target.value })} />
              </div>
            )}

            <div style={{ fontSize: 12, color: '#64748b' }}>
              {mode === 'known'
                ? <>Aucun QR : le dossier est déjà au complet. Les mails partent immédiatement et la
                    période ne s'écrit dans Zeus qu'<strong>à la validation</strong> — le profil apparaît en
                    Présence le 1er jour (tout de suite si le contrat est en cours), puis est masqué après la fin.</>
                : <>Lien/QR à <strong>usage unique</strong>, valable <strong>48h</strong>. Pour des intérimaires,
                    utilisez plutôt « 👷 Demander des intérimaires » (mail à l'équipe contrats, comptes créés à la confirmation).</>}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Envoi…' : (mode === 'known' ? 'Envoyer la prolongation' : 'Générer le lien + QR')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// --------------------------- QR affiché UNE fois -----------------------
function QrResultModal({ result, onClose }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <div className="modal-header">
          <h2>
            {result.prolongation
              ? `Prolongation envoyée${result.name ? ` — ${result.name}` : ''}`
              : `Invitation créée${result.candidateName ? ` — ${result.candidateName}` : ''}`}
          </h2>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>

        {/* Prolongation : aucun QR à afficher (result.qrPngB64 est absent) — on récapitule
            ce qui est parti et ce qu'il reste à faire. */}
        {result.prolongation ? (
          <div className="modal-body">
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 10, fontSize: 13, marginBottom: 10 }}>
              <div>{result.mailedContracts ? '✅' : '⚠️'} Demande à l'équipe contrats {result.mailedContracts ? 'envoyée' : 'NON envoyée'}</div>
              <div style={{ marginTop: 4 }}>
                {result.mailedCollaborator ? '✅' : '⚠️'} Mail au collaborateur{' '}
                {result.mailedCollaborator
                  ? <>envoyé à <strong>{result.candidateEmail}</strong> (confirmation 1 clic + guide d'encodage)</>
                  : <>NON envoyé{result.candidateEmail ? '' : ' — pas d\'email au dossier'}</>}
              </div>
            </div>
            <div style={{ fontSize: 13 }}>
              <strong>{result.contractType}</strong> du <strong>{result.contractStart}</strong>
              {result.contractEnd ? <> au <strong>{result.contractEnd}</strong></> : null}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 10 }}>
              La demande reste dans la liste ci-dessous. <strong>Validez-la quand le contrat revient</strong> :
              c'est à ce moment que la période est écrite dans Zeus. Le profil apparaît tout seul le 1er jour
              (tout de suite si le contrat est en cours) et se masque après la fin.
            </div>
          </div>
        ) : (
        <div className="modal-body" style={{ textAlign: 'center' }}>
          {result.mailed
            ? <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 10, color: '#166534', fontSize: 13, marginBottom: 10 }}>✅ Lien envoyé par email à <strong>{result.candidateEmail}</strong></div>
            : <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: 10, color: '#92400e', fontSize: 13, marginBottom: 10 }}>⚠️ Pas d'envoi automatique — transmettez le lien ou le QR (WhatsApp, impression…).</div>}
          <img src={`data:image/png;base64,${result.qrPngB64}`} alt="QR invitation" style={{ width: 240, height: 240 }} />
          <div style={{ fontSize: 12, wordBreak: 'break-all', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 8, margin: '10px 0' }}>{result.url}</div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button className="btn" onClick={() => { navigator.clipboard?.writeText(result.url); setCopied(true); }}>{copied ? '✅ Copié' : '📋 Copier le lien'}</button>
            <a className="btn" download={`invitation-${(result.candidateName || 'qr').replace(/\s+/g, '-')}.png`} href={`data:image/png;base64,${result.qrPngB64}`}>⬇️ Télécharger le QR</a>
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 10 }}>
            ⚠️ Ce QR/lien ne sera <strong>plus jamais réaffiché</strong> (usage unique, 48h). Au besoin, annulez et recréez une invitation.
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

// ------------------ Demande d'intérimaires (variante B) ----------------
function InterimRequestModal({ groups, onClose, onCreated }) {
  const [f, setF] = useState({ count: 1, contractStart: '', contractEnd: '', groupId: '', motif: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!f.contractStart || !f.contractEnd || f.contractStart > f.contractEnd) { setErr('Période obligatoire (début ≤ fin).'); return; }
    setSaving(true);
    try {
      const r = await onboarding.interimRequest(f);
      alert(r.mailed
        ? `✅ Demande envoyée par mail à l'équipe contrats : ${r.count} intérimaire(s) du ${r.contractStart} au ${r.contractEnd}${r.groupName ? ` (équipe ${r.groupName})` : ''}.\n\nDès confirmation, cliquez « Confirmé → valider les comptes » sur la ligne de la demande.`
        : '⚠️ Demande enregistrée mais le mail n\'a pas pu partir — prévenez l\'équipe contrats vous-même.');
      onCreated();
    } catch (e2) { setErr(errMsg(e2)); } finally { setSaving(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Demander des intérimaires</h2>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body">
            {err && <div className="alert alert-error">{err}</div>}
            <p style={{ fontSize: 13, color: '#64748b', marginTop: 0 }}>
              Un mail standardisé part vers l'équipe contrats (« merci de faire le nécessaire pour avoir N personnes intérim du … au … »).
              Les comptes seront prêts <strong>après confirmation</strong>, avec les noms des personnes (créés, ou repris pour celles qui ont déjà travaillé chez nous).
            </p>
            <div className="form-row" style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label>Nombre de personnes *</label>
                <input type="number" min={1} max={20} value={f.count} onChange={(e) => setF({ ...f, count: e.target.value })} required />
              </div>
              <div style={{ flex: 2 }}>
                <label>Équipe (groupe)</label>
                <select value={f.groupId} onChange={(e) => setF({ ...f, groupId: e.target.value })}>
                  <option value="">— Aucun —</option>
                  {(groups || []).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row" style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label>Du *</label>
                <input type="date" value={f.contractStart} onChange={(e) => setF({ ...f, contractStart: e.target.value })} required />
              </div>
              <div style={{ flex: 1 }}>
                <label>Au *</label>
                <input type="date" value={f.contractEnd} onChange={(e) => setF({ ...f, contractEnd: e.target.value })} required />
              </div>
            </div>
            <div className="form-row">
              <label>Motif <small style={{ color: '#94a3b8' }}>(optionnel — apparaît dans le mail)</small></label>
              <input type="text" maxLength={300} value={f.motif} onChange={(e) => setF({ ...f, motif: e.target.value })} placeholder="ex. surcroît préparation client X" />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Envoi…' : 'Envoyer la demande'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------- Confirmation intérim : saisie des noms → comptes -----------
// 06-10-2026 : un nom qui ressemble à un compte existant (désactivé compris) ne crée
// plus de doublon. Le serveur renvoie les comptes trouvés (409 DECISION_REQUISE) et
// chaque personne concernée reçoit un choix : reprendre ce compte — pré-coché quand un
// seul compte porte exactement ce nom et peut être repris — ou personne différente.
const LIBELLE_CONTRAT = { CDI: 'CDI', CDD: 'CDD', INTERIM: 'Intérim' };
// .form-row input met width:100% et un padding : neutralisés pour un bouton radio.
const RADIO = { width: 'auto', padding: 0, margin: '3px 0 0', flex: 'none' };

function InterimConfirmModal({ invite, onClose, onDone, onRefresh }) {
  const meta = invite.submittedData ? JSON.parse(invite.submittedData) : {};
  const [names, setNames] = useState(Array.from({ length: meta.count || 1 }, () => ''));
  // Par champ : comptes trouvés pour le nom envoyé, et le choix fait ('nouveau' ou id).
  const [trouves, setTrouves] = useState({});
  const [choix, setChoix] = useState({});
  // Par nom (ses mots, pas la position du champ) : comptes écartés par un « personne
  // différente » explicite — jamais pré-cochés ensuite, même si le nom change de champ.
  const [ecartes, setEcartes] = useState({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [info, setInfo] = useState('');
  // Délai dépassé, ou demande confirmée / annulée entre-temps : la fenêtre ne renvoie plus
  // rien (un nouveau clic effacerait la consigne et répondrait « déjà confirmée »).
  const [figee, setFigee] = useState(false);
  // Paires de noms proches déjà confirmées comme deux personnes (pas redemandé à chaque clic).
  const [doublonsOk, setDoublonsOk] = useState([]);
  // Le message s'affiche en haut de la fenêtre : ramené à la vue (on clique en bas).
  const haut = useRef(null);
  const [essai, setEssai] = useState(0);   // même message deux fois : ramené quand même
  useEffect(() => { if (err || info) haut.current?.scrollIntoView({ block: 'nearest' }); }, [err, info, essai]);

  const sans = (obj, i) => { const { [i]: _retire, ...reste } = obj; return reste; };
  const cleNom = (n) => motsDuNom(n).join(' ');
  const changerNom = (i, v) => {
    setNames(names.map((x, j) => (j === i ? v : x)));
    // Nom vraiment modifié (pas seulement la casse ou les espaces) : ce qui a été
    // trouvé pour l'ancien nom ne vaut plus.
    // Mots dans un autre ordre = vrai changement aussi (la pré-coche reposait sur l'ordre).
    const ordre = (n) => formesDuNom(n).ordre.join(' ');
    if (trouves[i] && (cleNom(v) !== cleNom(names[i]) || ordre(v) !== ordre(names[i]))) { setTrouves(sans(trouves, i)); setChoix(sans(choix, i)); }
  };
  // Un compte ne peut être repris que pour UNE personne (même règle à l'affichage et
  // pour la pré-coche).
  const prisDans = (choixes, id, i) => Object.entries(choixes).find(([j, v]) => Number(j) !== i && v === id);
  const prisAilleurs = (id, i) => prisDans(choix, id, i);
  // Les comptes montrés pour un champ, et s'ils étaient proposés comme reprenables.
  const vusPour = (i) => (trouves[i] || []).map((c) => ({
    id: c.id, reprenable: Boolean(c.reprenable), exact: Boolean(c.exact), memeOrdre: Boolean(c.memeOrdre),
    ...(c.aDossier !== undefined ? { aDossier: c.aDossier } : {}),
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (saving || figee) return;   // Entrée au clavier sur un bouton radio, par exemple
    setErr(''); setInfo(''); setEssai((n) => n + 1);
    // Même nettoyage que le serveur (espaces, apostrophes, forme Unicode) : ce qui
    // passe ici passe là-bas.
    // Un champ rempli doit être un nom complet : rien n'est écarté en silence
    // (une personne oubliée ne pourrait plus être ajoutée à la demande confirmée).
    const pasUnNom = names.map(nettoyerNom).find((n) => n && !estUnNom(n));
    if (pasUnNom) { setErr(`« ${pasUnNom} » n'est pas un nom complet : corrigez-le ou videz ce champ.`); return; }
    const lignes = names.map((n, i) => ({ i, name: nettoyerNom(n) })).filter((l) => l.name);
    if (!lignes.length) {
      setErr('Indiquez au moins un nom complet — une personne qui a déjà un compte se saisit aussi : Zeus propose de le reprendre. '
        + 'Si aucune ne peut être reprise ici, annulez la demande (bouton « Annuler » de sa ligne).');
      return;
    }
    // Même règle que le serveur (lib/noms.js) : la même personne deux fois aurait deux comptes.
    // Mêmes mots : bloquant. Noms seulement proches : deux personnes peut-être — TOUTES
    // les paires pas encore confirmées sont demandées en une fois, puis retenues.
    const paires = pairesEnDouble(lignes.map((l) => l.name));
    const exacte = paires.find((pr) => pr[2] === 'exact');
    if (exacte) { setErr(messageNomsEnDouble(lignes[exacte[0]].name, lignes[exacte[1]].name, 'exact')); return; }
    const cles = paires.map(([a, b]) => clePaire(lignes[a].name, lignes[b].name));
    const aConfirmer = paires.filter((_, k) => !doublonsOk.includes(cles[k]));
    if (aConfirmer.length) {
      const msg = aConfirmer.map(([a, b]) => messageNomsEnDouble(lignes[a].name, lignes[b].name, 'proche')).join('\n');
      if (!window.confirm(`${msg}\n\nCe sont bien, à chaque fois, des personnes différentes ?`)) return;
      setDoublonsOk(cles);
    }
    const doublonsAcceptes = cles;
    // Choix exigé seulement si un compte est reprenable ; sinon le clic revérifie (la
    // situation a pu changer depuis Utilisateurs) et le serveur reposera la question.
    const sansChoix = lignes.filter((l) => trouves[l.i]?.some((c) => c.reprenable) && !choix[l.i]);
    if (sansChoix.length) { setErr(`Choisissez pour : ${sansChoix.map((l) => l.name).join(', ')}.`); return; }
    const persons = lignes.map((l) => ({
      name: l.name,
      decision: !choix[l.i] ? null
        : choix[l.i] === 'nouveau' ? { type: 'nouveau', vus: vusPour(l.i) }
          : { type: 'reprendre', userId: choix[l.i], vus: vusPour(l.i) },
    }));
    setSaving(true);
    try { onDone(await onboarding.confirmInterim(invite.id, persons, { doublonsAcceptes })); }
    catch (e2) {
      const d = e2?.response?.data;
      if (d?.error === 'DECISION_REQUISE' && Array.isArray(d.details?.personnes)) {
        // Réponse alignée sur les noms envoyés (même ordre).
        const t = {};
        const c = { ...choix };
        const aRefaire = [];
        d.details.personnes.forEach((pers, k) => {
          const i = lignes[k]?.i;
          if (i === undefined) return;
          t[i] = pers.comptes || [];
          // Choix refusé : à refaire. Après un « personne différente » explicite, rien
          // n'est pré-coché à sa place (la personne l'avait écarté).
          if (pers.aChoisir) { if (c[i] !== 'nouveau') aRefaire.push([i, k]); delete c[i]; }
        });
        // Pré-coche APRÈS avoir retiré tous les choix refusés.
        aRefaire.forEach(([i, k]) => {
          const comptes = t[i];
          // Pré-coché seulement sans doute raisonnable : un seul compte, même nom complet
          // (deux mots au moins — un prénom seul ne prouve rien), reprenable, sans dossier
          // personnel (qui suivrait le compte : un homonyme y aurait accès).
          const seul = comptes.length === 1 ? comptes[0] : null;
          // Deux vrais mots au moins (une initiale « J » ne compte pas).
          const nomComplet = motsDuNom(lignes[k].name).filter((m) => m.length >= 2).length >= 2;
          if (seul && seul.exact && seul.memeOrdre && !seul.nomFiche && seul.reprenable && seul.aDossier === false
              && nomComplet && !prisDans(c, seul.id, i) && !(ecartes[cleNom(lignes[k].name)] || []).includes(seul.id)) c[i] = seul.id;
        });
        setTrouves(t); setChoix(c);
        setInfo(d.message);
      } else if (d?.error === 'ACCOUNTS_CHANGED') {
        setInfo(d.message);   // les choix restent : un nouveau clic revérifie
      } else if (['ALREADY_CONFIRMED', 'BAD_STATUS', 'GROUP_NOT_FOUND'].includes(d?.error)) {
        setErr(errMsg(e2));
        setFigee(true);
        onRefresh();          // la liste derrière se met à jour (sans clignoter)
      } else if (e2?.code === 'ECONNABORTED' || !e2?.response || (e2.response.status >= 502 && !d?.error)) {
        // Délai dépassé, réponse perdue, ou passerelle (502/503/504) : issue inconnue.
        setErr("La réponse tarde. Fermez cette fenêtre et regardez la liste avant de recommencer : si la demande "
          + 'apparaît confirmée, les comptes sont prêts — réinitialisez depuis Utilisateurs le mot de passe des comptes '
          + "nouveaux, réactivés, ou repris dont la personne ne s'était jamais connectée (seuls les comptes repris "
          + "déjà utilisés gardent le leur). Sans le droit de modifier les comptes, demandez-le à un administrateur.");
        setFigee(true);   // la liste est rechargée à la fermeture de la fenêtre
      } else {
        setErr(errMsg(e2));
      }
      setSaving(false);
    }
  };

  return (
    // Pas de fermeture par un clic à côté : la saisie et les choix seraient perdus
    // (et, pendant l'envoi, la réponse). Seuls × et Annuler / Fermer ferment.
    <div className="modal-backdrop">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Confirmation agence — valider les comptes</h2>
          <button className="btn-close" onClick={onClose} disabled={saving}>×</button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body">
            <div ref={haut} />
            {err && <div className="alert alert-error">{err}</div>}
            {info && (
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e3a8a', borderRadius: 8, padding: '8px 10px', fontSize: 13, marginBottom: 10 }}>
                {info}
              </div>
            )}
            <p style={{ fontSize: 13, color: '#64748b', marginTop: 0 }}>
              Demande : <strong>{meta.count} intérimaire(s)</strong> du <strong>{invite.contractStart}</strong> au <strong>{invite.contractEnd}</strong>
              {meta.groupName ? <> — équipe <strong>{meta.groupName}</strong></> : null}.
              Saisissez les noms reçus (laissez vide si l'agence a fourni moins de personnes).
              Si une personne a déjà un compte chez nous, Zeus le retrouve et vous propose de le reprendre plutôt que d'en créer un second.
            </p>
            {names.map((n, i) => (
              <div className="form-row" key={i}>
                <label>Personne {i + 1}</label>
                <input type="text" value={n} maxLength={150} placeholder="NOM Prénom" disabled={saving || figee}
                  onChange={(e) => changerNom(i, e.target.value)} />
                {trouves[i]?.length > 0 && (
                  <div role="radiogroup" aria-label={`Compte existant pour la personne ${i + 1}`}
                    style={{ marginTop: 6, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px', fontSize: 13, display: 'grid', gap: 6 }}>
                    <div style={{ fontWeight: 600 }}>Déjà un compte chez nous ?</div>
                    {trouves[i].map((c) => {
                      const ailleurs = prisAilleurs(c.id, i);
                      const possible = c.reprenable && !ailleurs;
                      return (
                        <label key={c.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: possible ? 'pointer' : 'not-allowed', opacity: possible ? 1 : 0.65 }}>
                          <input type="radio" name={`choix-${i}`} style={RADIO} disabled={!possible || saving || figee}
                            checked={choix[i] === c.id}
                            onChange={() => {
                              setChoix({ ...choix, [i]: c.id });
                              const cle = cleNom(names[i]);
                              if (ecartes[cle]?.includes(c.id)) setEcartes({ ...ecartes, [cle]: ecartes[cle].filter((x) => x !== c.id) });
                            }} />
                          <span>
                            Reprendre le compte de <strong>{c.name}</strong>{' '}
                            <small style={{ color: '#64748b' }}>
                              ({c.isActive ? 'actif' : 'désactivé'}
                              {c.contractType && c.contractType !== 'CDI' && c.contractStart
                                ? ` · dernier contrat ${LIBELLE_CONTRAT[c.contractType] || c.contractType} ${c.contractStart} → ${c.contractEnd || '…'}`
                                : ` · ${LIBELLE_CONTRAT[c.contractType] || c.contractType || 'sans contrat'}`}
                              {c.groupName ? ` · ${c.groupName}${c.equipeArchivee ? ' (archivée)' : ''}` : ''}
                              {c.reprenable && !meta.groupId && c.equipeArchivee ? ` · quittera cette équipe archivée (sans équipe, comme un compte neuf)` : ''}
                              {c.autresEquipes?.length ? ` · aussi dans : ${c.autresEquipes.join(', ')}` : ''}
                              {c.droitsIndividuels ? ` · ${c.droitsIndividuels} droit(s) accordé(s) individuellement, gardé(s)` : ''}
                              {c.heuresSemaine != null ? ` · ${c.heuresSemaine} h/semaine (gardées)` : ''}
                              {c.reprenable && meta.groupId && c.groupId !== meta.groupId
                                ? (c.equipeGardeeJusquAu
                                  ? ` · reste dans l'équipe ${c.groupName || 'actuelle'} (contrat en cours jusqu'au ${c.equipeGardeeJusquAu}) : passez-le à l'équipe ${meta.groupName || 'de la demande'} depuis Utilisateurs après cette date`
                                  : ` · passera ${c.groupName ? `de l'équipe ${c.groupName} ` : ''}à l'équipe ${meta.groupName || 'de la demande'}`)
                                : ''}
                              {c.nomFiche ? ` · retrouvé par le Nom/Prénom de sa fiche : ${c.nomFiche}` : ''}
                              {!c.exact ? ' · nom proche, pas identique' : !c.memeOrdre ? ' · mêmes mots, dans un autre ordre' : ''})
                            </small>
                            {!c.reprenable && <small style={{ display: 'block', color: '#b45309' }}>Impossible : {c.raison}.</small>}
                            {c.reprenable && c.aDossier && (
                              <small style={{ display: 'block', color: '#b45309' }}>
                                Ce compte a des données personnelles (email, adresse, carte d'identité…) qui le suivront : vérifiez que c'est bien la même personne.
                              </small>
                            )}
                            {c.reprenable && ailleurs && <small style={{ display: 'block', color: '#b45309' }}>Déjà choisi pour la personne {Number(ailleurs[0]) + 1}.</small>}
                          </span>
                        </label>
                      );
                    })}
                    {!trouves[i].some((c) => c.reprenable) && (
                      <small style={{ display: 'block', color: '#b45309' }}>
                        {trouves[i].some((c) => c.attendAdmin)
                          ? 'Un administrateur peut reprendre ce compte : fermez cette fenêtre et laissez-lui cette demande à confirmer.'
                          : 'Aucun de ces comptes ne peut être repris ici. Si c\'est la même personne, videz ce champ (voir la raison ci-dessus).'}
                        {' '}« Personne différente » crée un second compte.
                      </small>
                    )}
                    <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
                      <input type="radio" name={`choix-${i}`} style={RADIO} disabled={saving || figee}
                        checked={choix[i] === 'nouveau'}
                        onChange={() => {
                          setChoix({ ...choix, [i]: 'nouveau' });
                          const cle = cleNom(names[i]);
                          setEcartes({ ...ecartes, [cle]: [...new Set([...(ecartes[cle] || []), ...trouves[i].map((c) => c.id)])] });
                        }} />
                      <span>Personne différente — créer un nouveau compte</span>
                    </label>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn" onClick={onClose} disabled={saving}>{figee ? 'Fermer' : 'Annuler'}</button>
            {!figee && <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Validation…' : 'Valider les comptes'}</button>}
          </div>
        </form>
      </div>
    </div>
  );
}

// ------- Identifiants intérim — affichés UNE SEULE fois ----------------
function InterimCredsModal({ data, onClose }) {
  return (
    // Pas de fermeture par un clic à côté : les mots de passe ne sont montrés qu'une fois.
    <div className="modal-backdrop">
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h2>Comptes intérim prêts</h2>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 13 }}>
            ✅ {data.created.length} compte(s) prêt(s)
            {data.created.some((c) => c.reprise)
              ? <> — dont {data.created.filter((c) => c.reprise).length} <strong>repris</strong> (compte existant : nouveau contrat ajouté à son historique)</>
              : null}
            {' '}— visibles en Présence dès le premier jour du contrat (tout de suite s'il est en cours ; masqués s'il est déjà terminé) ;
            un compte repris déjà visible n'est pas masqué en attendant.
          </p>
          {data.avertissement && <div className="alert alert-error">{data.avertissement}</div>}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead><tr>{['Nom', 'Identifiant', 'Mot de passe temporaire'].map((h) => <th key={h} style={{ textAlign: 'left', borderBottom: '1px solid #e2e8f0', padding: 6 }}>{h}</th>)}</tr></thead>
            <tbody>
              {data.created.map((c) => (
                <tr key={c.userId}>
                  <td style={{ padding: 6 }}>
                    {c.name}
                    {c.reprise && <small style={{ marginLeft: 6, background: '#e0f2fe', color: '#075985', borderRadius: 4, padding: '1px 6px' }}>{c.etaitActif ? 'compte repris' : 'compte réactivé'}</small>}
                    {c.equipeQuittee && (
                      <small style={{ display: 'block', color: '#b45309' }}>
                        Sans équipe : son équipe {c.equipeQuittee} est archivée — choisissez-lui une équipe depuis Utilisateurs.
                      </small>
                    )}
                    {c.equipeGardee && (
                      <small style={{ display: 'block', color: '#b45309' }}>
                        Reste dans l'équipe {c.equipeGardee} jusqu'au {c.jusquAu} : changez son équipe depuis Utilisateurs après cette date.
                      </small>
                    )}
                  </td>
                  <td style={{ padding: 6, fontWeight: 700 }}>{c.username}</td>
                  <td style={{ padding: 6, fontWeight: c.tempPassword ? 700 : 400 }}>
                    {c.tempPassword || <span style={{ color: '#64748b' }}>inchangé — son mot de passe habituel</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.created.some((c) => c.tempPassword) ? (
          <div style={{ marginTop: 10, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: 10, fontSize: 12, color: '#92400e' }}>
            ⚠️ Ces mots de passe sont affichés <strong>une seule fois</strong> — notez-les et remettez-les aux personnes
            le premier jour. À la première connexion, chacun devra choisir son propre mot de passe.
            Un compte repris qui était désactivé reçoit lui aussi un nouveau mot de passe (l'ancien ne marche plus) ;
            un compte repris encore actif garde le sien — sauf s'il ne l'avait jamais choisi : il en reçoit alors un nouveau.
          </div>
          ) : (
            <p style={{ marginTop: 10, fontSize: 12, color: '#475569' }}>Aucun mot de passe à remettre : les comptes repris gardent le leur.</p>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

// --------------------------- Résultat validation -----------------------
function ValidatedModal({ data, onClose }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <div className="modal-header">
          <h2>Dossier validé</h2>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p>✅ Compte <strong>{data.username}</strong> {data.known ? 'mis à jour (collaborateur connu)' : 'créé'} —
            visible en Présence dès le premier jour du contrat (tout de suite s'il est en cours ; masqué s'il est déjà terminé).</p>
          {data.known ? (
            <>
              <p style={{ fontSize: 13, color: '#64748b' }}>Collaborateur connu : il garde ses identifiants existants.</p>
              {/* 23-10-2026 : l'adresse sert à réinitialiser le mot de passe — le formulaire public ne la change pas */}
              {data.emailNonRepris && (
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: 10, fontSize: 13, color: '#92400e' }}>
                  📧 Le formulaire donne une autre adresse e-mail : <strong>{data.emailNonRepris}</strong>. Elle n'a PAS été reprise :
                  l'adresse sert à réinitialiser le mot de passe. Si elle est bonne, changez-la dans la fiche (Utilisateurs → ✏️).
                </div>
              )}
            </>
          ) : data.credentialsMailed ? (
            <p style={{ fontSize: 13, color: '#166534' }}>Les identifiants temporaires ont été envoyés par email au candidat (avec le guide d'encodage si disponible).</p>
          ) : (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: 10, fontSize: 13, color: '#92400e' }}>
              ⚠️ L'email des identifiants n'a pas pu partir. Mot de passe temporaire à transmettre vous-même :
              <div style={{ fontSize: 18, fontWeight: 700, marginTop: 6, textAlign: 'center' }}>{data.tempPassword}</div>
              (affiché une seule fois — l'utilisateur devra le changer à la première connexion)
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}
